import { BehaviorSubject, Observable, throwError } from "rxjs";
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHandlerFn, HttpInterceptor, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { catchError, filter, take } from "rxjs/operators";
import { switchMap, of, finalize } from "rxjs";
import { MessageService } from 'primeng/api';
import { Router } from "@angular/router";
import { AuthService } from "../../auth/_services/auth.service";
import { TranslateService } from '@ngx-translate/core';
import { LocalizeRouterService } from "@gilsdav/ngx-translate-router";
import { SignalrService } from "../../panel/_services/signalr.service";

export let isRefreshingToken: boolean;
export let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const handle401Error = (request: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService) => {
  if (!isRefreshingToken) {
    isRefreshingToken = true;

    // Reset here so that the following requests wait until the token
    // comes back from the refreshToken call.
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((result: any) => {
        if (result) {
          isRefreshingToken = false;
          refreshTokenSubject.next(result.token.accessToken);
          return next(
            addTokenToRequest(request, result.token.accessToken)
          );
        }
        return of(authService.logout() as any);
      }),
      catchError((err) => {
        isRefreshingToken = false;
        return of(authService.logout() as any);
      }),
      finalize(() => {
        isRefreshingToken = false;
      })
    );
  }
  return refreshTokenSubject.pipe(
    filter((token) => token != null),
    take(1),
    switchMap((token) => {
      return next(addTokenToRequest(request, token));
    })
  );
}

export const addTokenToRequest = (
  request: HttpRequest<any>,
  token: string
): HttpRequest<any> => {
  if (request.url.indexOf("map.ir") > -1) {
    return request;
  }

  return request.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

export const handel498Error = (request: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService, router: Router) => {
  let authorizedTenants: any[] = [];
  let authorizedTenantsString = localStorage.getItem('tenants');
  if (authorizedTenantsString) {
    authorizedTenants = JSON.parse(authorizedTenantsString);
  }
  
  // Safely get current tenant ID
  let currentTenantId: any = null;
  const storedTenant = localStorage.getItem('tenant');
  if (storedTenant) {
    try {
      const parsedTenant = JSON.parse(storedTenant);
      currentTenantId = parsedTenant?.id;
    } catch (e) {
      console.warn('Error parsing tenant in error interceptor', e);
    }
  }
  
  if (authService.isSuperAdmin) {
    return next(request)
  } else if (authService.isSuperUser || authService.isAdmin) {
    return router.navigate(['/panel/change-password'], { queryParams: { returnUrl: router.url } });
  } else if (authorizedTenants?.filter((x: any) => x.roles.indexOf('TenantAdmin') > -1 || x.roles.indexOf('TeamMember') > -1)?.length) {
    if (currentTenantId && authorizedTenants?.filter((x: any) => x.id == currentTenantId && x.isUserEnabled)?.length) {
      return router.navigate(['/panel/change-password'], { queryParams: { returnUrl: router.url } });
    } else {
      return router.navigate(['/panel/founder/change-password'], { queryParams: { returnUrl: router.url } });
    }
  }
  else {
    return router.navigate(['/panel/founder/change-password'], { queryParams: { returnUrl: router.url } });
  }
}

export const ErrorServiceInterceptor: HttpInterceptorFn = (req, next) => {
  let messageService = inject(MessageService);
  let signalrService = inject(SignalrService);
  let router = inject(Router);
  let authService = inject(AuthService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {

      if (err.status === 400 && err.url?.indexOf('refreshToken') === -1) {
        if (err.error instanceof Object) {
          let text = '';
          if (err.error.errors) {
            for (const key in err.error.errors) {
              if (Object.prototype.hasOwnProperty.call(err.error.errors, key)) {
                const element = err.error.errors[key];
                text = element.join('/n');
              }
            }
          }

          if (text)
            messageService.add({
              severity: 'error',
              detail: text,
              closable: true,
              life: 10000
            });
        } else {
          messageService.add({
            severity: 'error',
            detail: err.error,
            closable: true,
            life: 10000
          });
        }
      }
      if (err.status === 401) {
        signalrService.stopConnection();
        return handle401Error(req, next, authService);
      }
      if (err.status === 403) {
        messageService.add({
          severity: 'error',
          detail: 'Access Denied',
          closable: true,
          life: 10000
        });
      }
      if (err.status === 498) {
        return handel498Error(req, next, authService, router);
      }
      if (err.status === 497) {
        return router.navigate(['/users/info'], { queryParamsHandling: 'preserve' });
      }
      return throwError(err);
    })
  );
};

@Injectable({
  providedIn: 'root'
})
export class ErrorInterceptor implements HttpInterceptor {
  isRefreshingToken: any;
  refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  errorMessage: any;
  constructor(
    // private loaderService: LoaderService
    private messageService: MessageService,
    private router: Router,
    private authService: AuthService,
    private signalrService: SignalrService,
    private translate: TranslateService,
    private localize: LocalizeRouterService
  ) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        // this.loaderService.hide();
        this.errorMessage = this.translate
          .get("خطا در ثبت مدارک !")
          .subscribe((message) => {
            this.errorMessage;
          });
        console.log(this.errorMessage);

        if (err.status === 400 && err.url?.indexOf('refreshToken') === -1) {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR'),
            detail: err.error,
            closable: true,
            life: 10000
          });
        }
        if (err.status === 401) {
          this.signalrService.stopConnection();
          return this.handle401Error(req, next);
        }
        if (err.status === 498) {
          return this.handel498Error(req, next, this.authService, this.router);
        }
        if (err.status === 497) {
          return this.router.navigate([this.localize.translateRoute('/users/info')], { queryParamsHandling: 'preserve' });
        }
        return throwError(err);
      })
    );
  }

  handle401Error = (request: HttpRequest<any>, next: HttpHandler) => {
    if (!isRefreshingToken) {
      isRefreshingToken = true;

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      refreshTokenSubject.next(null);

      let authService = inject(AuthService);

      return authService.refreshToken().pipe(
        switchMap((result: any) => {
          if (result) {
            isRefreshingToken = false;
            refreshTokenSubject.next(result.token.accessToken);
            return next.handle(
              addTokenToRequest(request, result.token.accessToken)
            );
          }
          return of(authService.logout() as any);
        }),
        catchError((err) => {
          isRefreshingToken = false;
          return of(authService.logout() as any);
        }),
        finalize(() => {
          isRefreshingToken = false;
        })
      );
    }
    return refreshTokenSubject.pipe(
      filter((token) => token != null),
      take(1),
      switchMap((token) => {
        return next.handle(addTokenToRequest(request, token));
      })
    );
  }

  handel498Error = (request: HttpRequest<any>, next: HttpHandler, authService: AuthService, router: Router) => {
    let authorizedTenants: any[] = [];
    let authorizedTenantsString = localStorage.getItem('tenants');
    if (authorizedTenantsString) {
      authorizedTenants = JSON.parse(authorizedTenantsString);
    }
    
    // Safely get current tenant ID
    let currentTenantId: any = null;
    const storedTenant = localStorage.getItem('tenant');
    if (storedTenant) {
      try {
        const parsedTenant = JSON.parse(storedTenant);
        currentTenantId = parsedTenant?.id;
      } catch (e) {
        console.warn('Error parsing tenant in error interceptor', e);
      }
    }
    
    if (authService.isSuperAdmin) {
      return next.handle(request)
    } else if (authService.isSuperUser || authService.isAdmin) {
      return router.navigate(['/panel/change-password'], { queryParams: { returnUrl: router.url } });
    } else if (authorizedTenants?.filter((x: any) => x.roles.indexOf('TenantAdmin') > -1 || x.roles.indexOf('TeamMember') > -1)?.length) {
      if (currentTenantId && authorizedTenants?.filter((x: any) => x.id == currentTenantId && x.isUserEnabled)?.length) {
        return router.navigate(['/panel/change-password'], { queryParams: { returnUrl: router.url } });
      } else {
        return router.navigate(['/panel/founder/change-password'], { queryParams: { returnUrl: router.url } });
      }
    }
    else {
      return router.navigate(['/panel/founder/change-password'], { queryParams: { returnUrl: router.url } });
    }
  }
}


